import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper functions for common API calls
export const projectsApi = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id, confirmName) => api.delete(`/projects/${id}`, { data: { confirmName } }),
  getPermissions: (id) => api.get(`/projects/${id}/permissions`),
  setPermission: (id, data) => api.post(`/projects/${id}/permissions`, data),
  removePermission: (projectId, userId) => api.delete(`/projects/${projectId}/permissions/${userId}`)
};

export const filesApi = {
  getByProject: (projectId, params) => api.get(`/files/project/${projectId}`, { params }),
  getExtensions: (projectId) => api.get(`/files/project/${projectId}/extensions`),
  getById: (id) => api.get(`/files/${id}`),
  getContent: (id) => api.get(`/files/${id}/content`, { responseType: 'blob' }),
  download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  upload: (projectId, file, folderId, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folderId', folderId);
    return api.post(`/files/project/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
    });
  },
  updateNotes: (id, notes) => api.put(`/files/${id}/notes`, { notes }),
  archive: (id, archived) => api.put(`/files/${id}/archive`, { archived }),
  delete: (id) => api.delete(`/files/${id}`),
  bulkDelete: (fileIds, projectId) => api.post('/files/bulk-delete', { fileIds, projectId }),
  bulkArchive: (fileIds, archived, projectId) => api.post('/files/bulk-archive', { fileIds, archived, projectId }),
  move: (fileId, folderId) => api.put(`/files/${fileId}/move`, { folderId })
};

export const foldersApi = {
  getByProject: (projectId, parentId) => api.get(`/folders/project/${projectId}`, {
    params: { parentId: parentId || 'null' }
  }),
  getById: (id) => api.get(`/folders/${id}`),
  create: (projectId, name, parentId) => api.post(`/folders/project/${projectId}`, { name, parentId }),
  rename: (folderId, name) => api.put(`/folders/${folderId}`, { name }),
  delete: (folderId) => api.delete(`/folders/${folderId}`),
  bulkDelete: (folderIds, projectId) => api.post('/folders/bulk-delete', { folderIds, projectId })
};

export const usersApi = {
  getAll: () => api.get('/auth/users'),
  getProfilePicture: (userId) => api.get(`/auth/profile-picture/${userId}`, { responseType: 'blob' })
};

export const sharesApi = {
  create: (data) => api.post('/shares', data),
  getByFileId: (fileId) => api.get(`/shares/file/${fileId}`),
  delete: (shareId) => api.delete(`/shares/${shareId}`)
};

// Public shares API (no auth required)
export const publicSharesApi = {
  getInfo: (shareId) => axios.get(`${API_URL}/shares/${shareId}`),
  verifyPassword: (shareId, password) => axios.post(`${API_URL}/shares/${shareId}/verify`, { password }),
  getContent: (shareId, password) => axios.get(`${API_URL}/shares/${shareId}/content`, {
    params: { password },
    responseType: 'blob'
  }),
  download: (shareId, password) => axios.get(`${API_URL}/shares/${shareId}/download`, {
    params: { password },
    responseType: 'blob'
  })
};
